import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  model,
  OnInit,
  output,
  QueryList,
  signal,
  Signal,
  ViewChildren,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { LassoSelectionDirective } from '../../directives/lasso-selection.directive';
import { TableOverlayComponent } from './table-creation/table-overlay/table-overlay.component';
import { TableOverlayStoreService } from '../../service/overlay/table-overlay-store.service';
import { TextSelectionOverlayComponent } from './text-selection/text-selection-overlay/text-selection-overlay.component';
import { TableCreationOverlayComponent } from './table-creation/table-creation-overlay/table-creation-overlay.component';
import { OcrService } from '../../service/ocr/ocr.service';
import { OcrSelectionService } from '../../service/ocr/ocr-selection.service';
import {
  ClaimsToastService,
  CtPage,
  Dimension2d,
  OverlayDimensionService,
  WordRect,
} from '@ps-gui/claims-terminal-shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LineOrientation, pointRect } from '../../models/interfaces';
import { TableData } from '../../table-overlay/table-overlay';
import { WorkflowService } from '../../service/app-state/workflow.service';
import {
  isConsolidationView,
  isExtractionView,
  isTableRowClassificationView,
} from '../../models/app-view';
import { isTableModel } from '../../models/domain-extraction-specifications';
import { OcrHighlightService } from '../../service/overlay/ocr-highlight.service';
import { FieldExtractionOverlayComponent } from './field-extraction/field-extraction-overlay.component';
import { CurrentFieldService } from '../../service/current-field.service';
import { DynamicFormService } from '@ps-gui/ui';
import { Keys } from '@ps-gui/shared-core';
import { tableSectionsByDomain } from '../../models/extraction-models/extraction-models';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import { ToolbarService } from '../../service/app-state/toolbar.service';

@Component({
  imports: [
    TableOverlayComponent,
    NgClass,
    TextSelectionOverlayComponent,
    TableCreationOverlayComponent,
    FieldExtractionOverlayComponent,
  ],
  selector: 'ct-extraction-ocr-overlay',
  templateUrl: './ocr-overlay.component.html',
})
export class OcrOverlayComponent implements OnInit {
  protected readonly boxSelection: LassoSelectionDirective = inject(LassoSelectionDirective);
  protected readonly highlightService = inject(OcrHighlightService);

  private readonly toolbarService = inject(ToolbarService);
  private readonly toastService = inject(ClaimsToastService);
  private readonly tableOverlayStoreService = inject(TableOverlayStoreService);
  private readonly dimensionService = inject(OverlayDimensionService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly ocrService = inject(OcrService);
  private readonly ocrSelectionService = inject(OcrSelectionService);
  private readonly workflowService = inject(WorkflowService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);

  protected readonly currentFieldService = inject(CurrentFieldService);
  protected readonly dynamicFormService = inject(DynamicFormService);

  readonly isTableCreationToggled = input.required<boolean>();
  readonly page = input.required<CtPage>();
  readonly isFieldExtractionOverlayCollapsed = input.required<boolean>();
  readonly enableAutoUpdate = model.required<boolean>();
  readonly ensureSingleOverlay = input(false);
  readonly selectedDocumentId = input<string | undefined>(undefined);

  fieldExtractionOverlayToggle = output<boolean>();

  @ViewChildren('tableOverlayComponent') tableOverlayComponent!: QueryList<TableOverlayComponent>;

  constructor() {
    Keys.Escape.down.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.currentFieldService.currentActiveField = undefined;
      this.highlightService.setFieldHighlight('', '');
    });
  }

  protected lastReleasedLasso: WordRect | undefined;

  protected readonly tableCollection = linkedSignal<TableData[]>(() =>
    this.tableOverlayStoreService.getTablesOnPage(this.page().id),
  );

  tables = this.tableCollection.asReadonly();

  protected readonly selectedWordRects: Signal<WordRect[]> = computed(() => {
    const lasso =
      this.boxSelection.currentLasso() ?? pointRect(this.boxSelection.currentMousePosition());

    return this.ocrService.getWordsInRect(this.page().id, lasso);
  });

  protected readonly dimension = computed<Dimension2d | undefined>(
    () => this.dimensionService.dimensionsByPageId()[this.page().id],
  );

  protected readonly tableExtractionSectionsOnCurrentPage = computed(() => {
    const view = this.workflowService.currentView();
    return isExtractionView(view)
      ? view.page.sections.filter((section) => isTableModel(section))
      : isConsolidationView(view)
        ? tableSectionsByDomain[this.ctBatchStoreService.ctBatchDomain]
        : [];
  });

  protected readonly shouldDisplayTableOverlay = computed(
    () =>
      this.tableExtractionSectionsOnCurrentPage().length > 0 ||
      this.isCurrentPageRowClassification(),
  );

  protected readonly isCurrentPageRowClassification = computed(() =>
    isTableRowClassificationView(this.workflowService.currentView()),
  );

  // TODO: Implement some kind of "select which kind of table you want to extract"
  private readonly tableKey = computed(() => this.tableExtractionSectionsOnCurrentPage()[0]?.key);

  protected readonly highlight = computed(() => {
    const currentHighlight = this.highlightService.highlight();
    return currentHighlight?.pageId === this.page().id ? currentHighlight : undefined;
  });

  readonly showFieldExtractionOverlay = computed(() => {
    if (this.tableExtractionSectionsOnCurrentPage().length) {
      return false;
    }

    if (this.ensureSingleOverlay() && this.selectedDocumentId() !== this.page()?.documentId) {
      return false;
    }

    return this.highlightService.highlight()
      ? this.highlight()?.pageId === this.page()?.id
      : this.page()?.index === 0; //renders not attached overlay always on first ocr overlay
  });

  ngOnInit(): void {
    this.boxSelection.lastReleasedLasso$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lasso) => {
        this.createNewTable(lasso);
      });

    this.initReleasedLassoHandling();
  }

  private createNewTable(lasso: WordRect) {
    const tableKey = this.tableKey();
    if (!this.isTableCreationToggled() || !tableKey) return;

    this.lastReleasedLasso = lasso;
    if (!this.canCreateTable(lasso)) return;

    const newTable: TableData = {
      tableModelKey: tableKey,
      id: crypto.randomUUID(),
      pageId: this.page().id,
      confirmed: signal(false),
      layoutDefined: signal(false),
      lines: {
        vertical: signal([
          {
            offset: signal(lasso.x[0]),
            orientation: LineOrientation.VERTICAL,
            isBoundary: true,
          },
          {
            offset: signal(lasso.x[1]),
            orientation: LineOrientation.VERTICAL,
            isBoundary: true,
          },
        ]),
        horizontal: signal([
          {
            offset: signal(lasso.y[0]),
            orientation: LineOrientation.HORIZONTAL,
            isBoundary: true,
          },
          {
            offset: signal(lasso.y[1]),
            orientation: LineOrientation.HORIZONTAL,
            isBoundary: true,
          },
        ]),
      },
      columnHeaders: signal([]),
    };

    this.tableCollection.set(this.tableOverlayStoreService.storeTable(newTable));
  }

  protected deleteTable(id: string): void {
    this.tableCollection.set(
      this.tableOverlayStoreService.deleteTableOverlayAtPage(this.page().id, id),
    );
  }

  private canCreateTable(lasso: WordRect): boolean {
    const MIN_SIZE = 0.1;
    const size = lasso.x[1] - lasso.x[0] + lasso.y[1] - lasso.y[0];
    const areAllTablesConfirmed = !this.tableCollection().some((tableData) => {
      return !tableData.confirmed();
    });

    const isRectSizeEnough = size >= MIN_SIZE;
    if (!areAllTablesConfirmed) {
      this.toastService.error(
        'Die aktuelle Tabelle muss bestätigt werden bevor die nächste erstellt werden kann',
      );
    } else if (!isRectSizeEnough) {
      this.toastService.error('Ein größerer Bereich muss ausgewählt werden');
    }

    return size >= MIN_SIZE && areAllTablesConfirmed;
  }

  private initReleasedLassoHandling() {
    this.boxSelection.lastReleasedLasso$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selectedWordRects().length === 0) return;
      if (this.toolbarService.isTableCreationEnabled()) return;

      const selectedWords = this.selectedWordRects().map((rect) =>
        this.ocrService.getWord(this.page().id, rect),
      );
      this.ocrSelectionService.newSelection(
        selectedWords,
        this.selectedWordRects(),
        this.page().id,
      );
    });
  }

  updateTableData(tableData: TableData): void {
    this.tableOverlayStoreService.updateTableData(tableData);
  }
}
